import { fetchAllPages } from '@/lib/fetch-all-pages';
import { graphqlClient } from '@/lib/graphql-client';
import { taskDateRangeClause } from '../../utils/task-date';
import {
  buildGetDepartmentsLookupQuery,
  buildGetMbmProjectsLookupQuery,
  buildGetTimeSheetsQuery,
} from '../graphql/queries';
import {
  DepartmentsResult,
  GetTimeSheetsParams,
  MbmProjectsResult,
  ResourceBreakdown,
  TimeSheetRecord,
  TimeSheetTask,
  TimeSheetsPagingResult,
  UsersTimesheetFilter,
} from '../types/timesheet-manager.types';

/**
 * TasksJson is stored as a JSON-serialized string on the TimeSheet schema, so
 * every read must JSON.parse it into the domain TimeSheetTask[] shape -- same
 * pattern as BreakTimeDetails on Attendance (see
 * hr-my-attendance/services/attendance.service.ts) and already applied by the
 * sibling time-attendance/time-tracker/services/my-timesheet.service.ts.
 */
const parseTasksJson = (raw: unknown): TimeSheetTask[] => {
  if (!raw || typeof raw !== 'string') return Array.isArray(raw) ? raw : [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toTimeSheetRecord = (item: TimeSheetRecord & { TasksJson?: string }): TimeSheetRecord => ({
  ...item,
  Tasks: parseTasksJson(item.TasksJson),
});

/** Flat, paginated TimeSheet listing -- used for the per-user detail drill-down. */
export const getTimeSheetEntries = async (
  params: GetTimeSheetsParams
): Promise<{ TimeSheets: TimeSheetsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetTimeSheetsQuery(filter, pageNo, pageSize, order),
  })) as { TimeSheets: TimeSheetsPagingResult };
  return {
    TimeSheets: {
      ...result.TimeSheets,
      items: result.TimeSheets.items.map(toTimeSheetRecord),
    },
  };
};

// NOTE: MbmProject.Status values weren't confirmed against a live catalog
// (see time-attendance/time-tracker/services/my-timesheet.service.ts for the same call),
// so this doesn't filter by status.
export const getMbmProjectsLookup = async (): Promise<{ MbmProjects: MbmProjectsResult }> => {
  const result = (await graphqlClient.query({
    query: buildGetMbmProjectsLookupQuery({}, 1, 500),
  })) as { MbmProjects: MbmProjectsResult };
  return {
    MbmProjects: {
      ...result.MbmProjects,
      items: [...result.MbmProjects.items].sort((a, b) =>
        a.ProjectName.localeCompare(b.ProjectName)
      ),
    },
  };
};

const getDepartmentsLookup = async (): Promise<{ Departments: DepartmentsResult }> => {
  const result = (await graphqlClient.query({
    query: buildGetDepartmentsLookupQuery({}, 1, 200),
  })) as { Departments: DepartmentsResult };
  return {
    Departments: {
      ...result.Departments,
      items: [...result.Departments.items].sort((a, b) => a.Title.localeCompare(b.Title)),
    },
  };
};

/** Records per request while the rollup reads its range. */
const ROLLUP_PAGE_SIZE = 500;

/**
 * Ports the Angular app-timesheet-manager's resource -> customer -> project
 * rollup (TimesheetManagerComponent.processTimesheetData /
 * ITimesheet-manager.interface.ts: IResource / ICustomer / IProject). Blocks
 * GraphQL has no custom rollup resolver, so this queries the flat TimeSheets
 * schema (record-level filtered by department/date range/customer/project as
 * a coarse pre-filter) and groups client-side, mirroring the technique
 * already established by hr-leave-calendar/services/leave-calendar.service.ts.
 *
 * A single TimeSheet record can carry tasks for several projects/customers at
 * once (Tasks[]), so customer/project filters are re-applied per-task after
 * the coarse record-level fetch -- an array-contains match on CustomerIds/
 * ProjectIds alone would over-include tasks that don't actually match.
 */
export const getUsersTimesheetRollup = async (
  filter: UsersTimesheetFilter
): Promise<ResourceBreakdown[]> => {
  const { startDate, endDate, departmentId, customerId, projectId, displayName, userId } = filter;

  const conditions: Record<string, unknown> = {};
  if (startDate && endDate) {
    conditions.TaskDate = taskDateRangeClause(startDate, endDate);
  }
  if (departmentId) conditions.DepartmentId = departmentId;
  if (customerId) conditions.CustomerIds = { $in: [customerId] };
  if (projectId) conditions.ProjectIds = { $in: [projectId] };
  if (displayName) conditions.DisplayName = { $regex: displayName, $options: 'i' };
  if (userId) conditions.UserId = userId;

  // Every record of the range: the rollup totals them, so a missing page would undercount.
  const [timeSheets, departmentsResult] = await Promise.all([
    fetchAllPages(async (pageNo, pageSize) => {
      const result = (await graphqlClient.query({
        query: buildGetTimeSheetsQuery(conditions, pageNo, pageSize),
      })) as { TimeSheets: TimeSheetsPagingResult };
      return result.TimeSheets;
    }, ROLLUP_PAGE_SIZE),
    getDepartmentsLookup(),
  ]);

  const departmentNameById = new Map(
    departmentsResult.Departments.items.map((department) => [department.ItemId, department.Title])
  );

  // Records come latest change first, so employees are listed in the order the old rollup listed them.
  const records = timeSheets.map(toTimeSheetRecord);
  const resources = new Map<string, ResourceBreakdown>();

  for (const record of records) {
    for (const task of record.Tasks) {
      if (customerId && task.CustomerId !== customerId) continue;
      if (projectId && task.ProjectId !== projectId) continue;

      let resource = resources.get(record.UserId);
      if (!resource) {
        resource = {
          UserId: record.UserId,
          ResourceName: record.DisplayName ?? '',
          ResourceEmail: record.Email,
          DepartmentId: record.DepartmentId,
          DepartmentName: record.DepartmentId
            ? departmentNameById.get(record.DepartmentId)
            : undefined,
          Customers: [],
          GrandTotal: 0,
        };
        resources.set(record.UserId, resource);
      }

      const customerKey = task.CustomerId ?? 'unassigned';
      let customer = resource.Customers.find((c) => c.CustomerId === customerKey);
      if (!customer) {
        customer = {
          CustomerId: customerKey,
          CustomerName: task.CustomerName ?? 'Unassigned',
          Projects: [],
        };
        resource.Customers.push(customer);
      }

      const projectKey = task.ProjectId ?? 'unassigned';
      let project = customer.Projects.find((p) => p.ProjectId === projectKey);
      if (!project) {
        project = {
          ProjectId: projectKey,
          ProjectName: task.ProjectName ?? 'Unassigned',
          Billable: 0,
          GeneralAdmin: 0,
          SalesAndMarketing: 0,
          Total: 0,
        };
        customer.Projects.push(project);
      }

      project.Billable += task.Billable;
      project.GeneralAdmin += task.GeneralAdmin;
      project.SalesAndMarketing += task.SalesAndMarketing;
      const taskTotal = task.Billable + task.GeneralAdmin + task.SalesAndMarketing;
      project.Total += taskTotal;
      resource.GrandTotal += taskTotal;
    }
  }

  return Array.from(resources.values());
};
